import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import useToast from '../../hooks/useToast';
// import { useAuth } from '../../contexts/AuthContext'; // Unused, removing
import Select from '../../components/ui/Select';
import MemberTable from './components/MemberTable';
import MemberCard from './components/MemberCard';
import InviteMemberModal from '../../components/modals/InviteMemberModal';
import EditRoleModal from './components/EditRoleModal';
import RemoveMemberModal from './components/RemoveMemberModal';
import MemberProfileModal from './components/MemberProfileModal';
import { useUserProfile } from '../../hooks/useUserProfile';
import BulkActionsBar from './components/BulkActionsBar';
import { useTeamMembers } from './hooks/useTeamMembers';
import apiService from '../../utils/apiService.js';
import teamService from '../../utils/teamService';
import sessionService from '../../utils/sessionService.js';

const TeamMembers = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc',
  });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('members'); // 'members' or 'requests'

  // Modal states
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const { userProfile: currentUser, currentOrganization, userRole: hookUserRole } = useUserProfile();
  // Toast notifications
  const { showToast } = useToast();
  
  const userRole = hookUserRole || currentOrganization?.role || 'member';

  // Get role-specific features
  const getRoleFeatures = (role) => {
    if (!role) return {
      canInviteMembers: false,
      canManageMembers: false,
      canRemoveMembers: false,
    };
    
    const features = {

      member: {
        canInviteMembers: false,
        canManageMembers: false,
        canRemoveMembers: false,
      },
      admin: {
        canInviteMembers: true,
        canManageMembers: true,
        canRemoveMembers: true,
      },
      owner: {
        canInviteMembers: true,
        canManageMembers: true,
        canRemoveMembers: true,
      },
    };
    return features[role.toLowerCase()] || features.member;
  };

  const roleFeatures = getRoleFeatures(userRole);

  const { 
    members, 
    joinRequests, 
    refresh: reloadTeamData 
  } = useTeamMembers(
    currentOrganization?.id, 
    currentUser, 
    userRole, 
    searchQuery, 
    roleFilter
  );

  // Setup WebSocket listeners for real-time updates
  useEffect(() => {
    if (!currentOrganization?.id) return;

    try {
      const wsMod = require('../../utils/websocketService.js');
      const websocketService = wsMod.default || wsMod;

      const onRefreshData = () => {
        console.log('🔄 Reloading team data due to WebSocket event...');
        reloadTeamData();
      };

      websocketService.on('memberJoined', onRefreshData);
      websocketService.on('invitationAccepted', onRefreshData);

      return () => {
        websocketService.off('memberJoined', onRefreshData);
        websocketService.off('invitationAccepted', onRefreshData);
      };
    } catch (error) {
      console.warn('WebSocket service not available:', error);
    }
  }, [currentOrganization?.id, reloadTeamData]);


  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'owner', label: 'Owner' },
    { value: 'admin', label: 'Admin' },
    { value: 'member', label: 'Member' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' },
  ];

  // Filter and sort members
  const filteredAndSortedMembers = useMemo(() => {
    let filtered = members.filter((member) => {
      const matchesSearch =
        (member.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (member.email || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole =
        roleFilter === 'all' || (member.role || 'member').toLowerCase() === roleFilter;
      
      // Map filter values to actual status values from backend
      // Backend uses: 'online', 'offline', 'away', 'pending'
      // Filter options: 'active' (maps to online), 'inactive' (maps to offline/away), 'pending'
      let matchesStatus = statusFilter === 'all';
      if (!matchesStatus) {
        const memberStatus = member.status?.toLowerCase() || '';
        if (statusFilter === 'active') {
          matchesStatus = memberStatus === 'online';
        } else if (statusFilter === 'inactive') {
          matchesStatus = memberStatus === 'offline' || memberStatus === 'away';
        } else {
          matchesStatus = memberStatus === statusFilter;
        }
      }

      return matchesSearch && matchesRole && matchesStatus;
    });

    // Sort members
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'lastActivity') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [members, searchQuery, roleFilter, statusFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const handleSelectMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMembers.length === filteredAndSortedMembers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(filteredAndSortedMembers.map((member) => member.id));
    }
  };

  const handleInviteMembers = async (inviteData) => {
    if (!currentOrganization?.id) {
      console.error('No organization selected');
      return;
    }

    try {
      console.log('Inviting members:', inviteData);

      // Send invitations through API
      for (const email of inviteData.emails) {
        await teamService.inviteTeamMember(currentOrganization.id, {
          email,
          role: inviteData.role,
          message: inviteData.message,
        });
      }

      // Reload team members to show updated list
      await reloadTeamData();
    } catch (error) {
      console.error('Failed to invite members:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEditRole = (member) => {
    setSelectedMember(member);
    setIsEditRoleModalOpen(true);
  };

  const handleUpdateRole = async (memberId, newRole) => {
    if (!currentOrganization?.id) {
      console.error('No organization selected');
      return;
    }

    try {
      console.log('Updating role:', memberId, newRole);

      await teamService.updateMemberRole(currentOrganization.id, memberId, {
        role: newRole,
      });

      setMembers((prev) =>
        prev.map((member) =>
          member.id === memberId ? { ...member, role: newRole } : member
        )
      );
    } catch (error) {
      console.error('Failed to update member role:', error);
      // You might want to show an error message to the user here
    }
  };

  // Helper function to update state locally after actions
  const setMembers = (updateFn) => {
    // This is a local update for UI responsiveness
    // In a real optimized app, you might use an optimistic UI update
    reloadTeamData();
  };



  const handleRemoveMember = (member) => {
    setSelectedMember(member);
    setIsRemoveModalOpen(true);
  };

  const handleConfirmRemove = async (memberId) => {
    if (!currentOrganization?.id) {
      console.error('No organization selected');
      showToast('No organization selected', 'error');
      return;
    }

    try {
      const member = members.find(m => m.id === memberId);
      const isPending = member?.status === 'pending';

      if (isPending) {
        await teamService.cancelInvitation(currentOrganization.id, memberId);
        showToast('Invitation cancelled successfully', 'success');
      } else {
        await teamService.removeMember(currentOrganization.id, memberId);
        showToast('Team member removed from organization successfully', 'success');
      }

      // Update local state immediately
      setMembers((prev) => prev.filter((member) => member.id !== memberId));
      setSelectedMembers((prev) => prev.filter((id) => id !== memberId));
      
      setIsRemoveModalOpen(false);
      setSelectedMember(null);
      
      // Refresh list to be sure
      await reloadTeamData();
    } catch (error) {
      console.error('Failed to remove member:', error);
      const msg = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`Failed to remove member: ${msg}`, 'error');
    }
  };
  const handleApproveRequest = async (requestId) => {
    try {
      await apiService.organizations.approveJoinRequest(currentOrganization.id, requestId);
      showToast('Join request approved successfully', 'success');
      await reloadTeamData();
    } catch (error) {
      showToast(error.message || 'Failed to approve request', 'error');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await apiService.organizations.rejectJoinRequest(currentOrganization.id, requestId);
      showToast('Join request rejected', 'warning');
      await reloadTeamData();
    } catch (error) {
      showToast(error.message || 'Failed to reject request', 'error');
    }
  };

  const handleBulkRoleChange = async (newRole) => {
    console.log('Bulk role change:', selectedMembers, newRole);
    // Mock API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setMembers((prev) =>
      prev.map((member) =>
        selectedMembers.includes(member.id)
          ? { ...member, role: newRole }
          : member
      )
    );
    setSelectedMembers([]);
  };

  const handleBulkRemove = async () => {
    console.log('Bulk remove:', selectedMembers);
    // Mock API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setMembers((prev) =>
      prev.filter((member) => !selectedMembers.includes(member.id))
    );
    setSelectedMembers([]);
  };

  const handleClearSelection = () => {
    setSelectedMembers([]);
  };

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setIsProfileModalOpen(true);
  };

  const location = useLocation();
  const processedOpenMemberIdRef = React.useRef(null);

  // Auto-open member profile if redirected from dashboard
  useEffect(() => {
    const openMemberId = location.state?.openMemberId;

    // Skip if we've already processed this ID or if no ID is present
    if (!openMemberId || openMemberId === processedOpenMemberIdRef.current) {
      return;
    }

    if (members.length > 0) {
      const targetMember = members.find(
        (m) => m.id === openMemberId || m.user_id === openMemberId
      );
      if (targetMember) {
        processedOpenMemberIdRef.current = openMemberId; // Mark as processed
        handleMemberClick(targetMember);
        // Clear navigation state to prevent re-opening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, members]);

  return (
    <div className='min-h-screen bg-transparent relative'>
      <RoleBasedHeader
        userRole={userRole ? userRole.toLowerCase() : 'member'}
        currentUser={
          currentUser
            ? {
                name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email?.split('@')[0] || 'User',
                email: currentUser.email,
                role: userRole,
              }
            : null
        }
        currentOrganization={currentOrganization}
      />

      <main className='pt-20'>
        <div className='max-w-7xl mx-auto px-4 sm:px-8 lg:px-10 py-10'>
          <div className="mb-6">
            <Breadcrumb />
          </div>

          {/* Page Header */}
          <div className='flex items-center justify-between mb-10'>
            <div className='flex items-center space-x-6'>
              <div className='w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/20'>
                <Icon
                  name='Users'
                  size={28}
                  className='text-white'
                />
              </div>
              <div>
                <h1 className='text-4xl font-black text-slate-800 dark:text-white tracking-tightest'>
                  Team Members
                </h1>
                <p className='text-slate-500 dark:text-slate-400 font-bold uppercase tracking-[0.15em] text-[10px]'>
                  Organization Workforce • {members.filter(m => m.status !== 'pending').length} members
                </p>
              </div>
            </div>
            
            <div className='flex items-center space-x-2'>
              {roleFeatures.canManageMembers && (
                <div className='flex p-1 bg-slate-100 dark:bg-slate-900 rounded-xl mr-4'>
                  <button
                    onClick={() => setActiveTab('members')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                      activeTab === 'members'
                        ? 'bg-white dark:bg-slate-800 shadow-sm text-primary'
                        : 'text-slate-500'
                    }`}
                  >
                    Members
                  </button>
                  <button
                    onClick={() => setActiveTab('requests')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${
                      activeTab === 'requests'
                        ? 'bg-white dark:bg-slate-800 shadow-sm text-primary'
                        : 'text-slate-500'
                    }`}
                  >
                    Join Requests
                    {joinRequests.length > 0 && (
                      <span className='w-5 h-5 bg-destructive text-white text-[10px] rounded-full flex items-center justify-center'>
                        {joinRequests.length}
                      </span>
                    )}
                  </button>
                </div>
              )}
              {roleFeatures.canInviteMembers && (
                <Button
                  onClick={() => setIsInviteModalOpen(true)}
                  className="rounded-xl px-6 py-3 shadow-lg hover:shadow-primary/20 transition-all font-bold"
                  iconName='UserPlus'
                  iconPosition='left'
                >
                  Assemble Team
                </Button>
              )}
            </div>
          </div>

          {/* Activity Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="glass-surface p-6 rounded-2xl flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Icon name="Users" size={24} />
              </div>
              <div>
                <div className="text-2xl font-black text-slate-800 dark:text-white">
                  {members.length}
                </div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Total Managed
                </div>
              </div>
            </div>

            <div className="glass-surface p-6 rounded-2xl flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Icon name="Activity" size={24} />
              </div>
              <div>
                <div className="text-2xl font-black text-slate-800 dark:text-white">
                  {members.filter(m => m.status === 'online').length}
                </div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Online Now
                </div>
              </div>
            </div>

            <div className="glass-surface p-6 rounded-2xl flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Icon name="Clock" size={24} />
              </div>
              <div>
                <div className="text-2xl font-black text-slate-800 dark:text-white">
                  {members.filter(m => {
                    if (m.status === 'online' || m.status === 'away') return true;
                    const ts = m.lastActivity ? new Date(m.lastActivity) : null;
                    return ts && (Date.now() - ts.getTime() <= 24 * 60 * 60 * 1000);
                  }).length}
                </div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Active (24h)
                </div>
              </div>
            </div>

            <div className="glass-surface p-6 rounded-2xl flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Icon name="Mail" size={24} />
              </div>
              <div>
                <div className="text-2xl font-black text-slate-800 dark:text-white">
                  {members.filter(m => m.status === 'pending').length}
                </div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Pending Invites
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search - Glass Surface */}
          <div className='glass-surface rounded-3xl p-8 mb-8'>
            <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6'>
              <div className='flex flex-col sm:flex-row sm:items-center gap-4 flex-1'>
                <div className='relative flex-1 max-w-md'>
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                    <Icon name="Search" size={18} className="text-slate-400" />
                  </div>
                  <input
                    type='text'
                    placeholder='Find team members...'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className='w-full pl-12 pr-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm font-medium'
                  />
                </div>
                <div className='flex gap-3'>
                  <Select
                    placeholder='Role'
                    options={roleOptions}
                    value={roleFilter}
                    onChange={setRoleFilter}
                    className='min-w-[140px]'
                    dropup={true}
                  />
                  <Select
                    placeholder='Status'
                    options={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    className='min-w-[140px]'
                    dropup={true}
                  />
                </div>
              </div>

              <div className='flex items-center gap-4'>
                <div className='flex items-center bg-slate-100 dark:bg-slate-900 rounded-xl p-1.5'>
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size='sm'
                    onClick={() => setViewMode('table')}
                    iconName='Table'
                    className={`h-9 w-9 p-0 rounded-lg ${viewMode === 'table' ? 'shadow-md' : ''}`}
                  />
                  <Button
                    variant={viewMode === 'cards' ? 'default' : 'ghost'}
                    size='sm'
                    onClick={() => setViewMode('cards')}
                    iconName='Grid3X3'
                    className={`h-9 w-9 p-0 rounded-lg ${viewMode === 'cards' ? 'shadow-md' : ''}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bulk Actions */}
          <BulkActionsBar
            selectedCount={selectedMembers.length}
            onBulkRoleChange={
              roleFeatures.canManageMembers ? handleBulkRoleChange : null
            }
            onBulkRemove={
              roleFeatures.canRemoveMembers ? handleBulkRemove : null
            }
            onClearSelection={handleClearSelection}
            canManageMembers={roleFeatures.canManageMembers}
            canRemoveMembers={roleFeatures.canRemoveMembers}
          />

          {/* Members Content - Glass Surface for Table */}
          <div className={`${viewMode === 'table' || activeTab === 'requests' ? 'glass-surface rounded-3xl overflow-hidden' : ''}`}>
            {activeTab === 'members' ? (
              viewMode === 'table' ? (
                <MemberTable
                  members={filteredAndSortedMembers}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  onEditRole={roleFeatures.canManageMembers ? handleEditRole : null}
                  onRemoveMember={
                    roleFeatures.canRemoveMembers ? handleRemoveMember : null
                  }
                  selectedMembers={selectedMembers}
                  onSelectMember={handleSelectMember}
                  onSelectAll={handleSelectAll}
                  onMemberClick={handleMemberClick}
                  canManageMembers={roleFeatures.canManageMembers}
                  canRemoveMembers={roleFeatures.canRemoveMembers}
                />
              ) : (
                <div className='p-4'>
                  <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6'>
                    {filteredAndSortedMembers.map((member) => (
                      <MemberCard
                        key={member.id}
                        member={member}
                        onEditRole={roleFeatures.canManageMembers ? handleEditRole : null}
                        onRemoveMember={
                          roleFeatures.canRemoveMembers ? handleRemoveMember : null
                        }
                        onClick={() => handleMemberClick(member)}
                        canManageMembers={roleFeatures.canManageMembers}
                        canRemoveMembers={roleFeatures.canRemoveMembers}
                      />
                    ))}
                  </div>
                  {filteredAndSortedMembers.length === 0 && (
                    <div className='py-20 text-center'>
                      <div className='w-20 h-20 bg-slate-100 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6'>
                        <Icon name='Users' size={40} className='text-slate-400' />
                      </div>
                      <h3 className='text-xl font-bold text-slate-800 dark:text-white mb-2'>
                        No team members found
                      </h3>
                      <p className='text-slate-500'>Try adjusting your search or filters</p>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className='p-8'>
                <div className='mb-6'>
                  <h3 className='text-xl font-bold text-slate-800 dark:text-white'>
                    Pending Join Requests
                  </h3>
                  <p className='text-sm text-slate-500'>
                    These users have requested to join your organization workspace.
                  </p>
                </div>
                
                {joinRequests.length === 0 ? (
                  <div className='py-20 text-center glass-surface rounded-3xl'>
                    <Icon name='CheckCircle' size={48} className='text-emerald-500 mx-auto mb-4' />
                    <h4 className='text-lg font-bold text-slate-800 dark:text-white'>
                      All caught up!
                    </h4>
                    <p className='text-slate-500'>No pending join requests.</p>
                  </div>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='w-full'>
                      <thead>
                        <tr className='border-b border-slate-100 dark:border-slate-800'>
                          <th className='text-left py-4 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider'>User</th>
                          <th className='text-left py-4 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider'>Requested At</th>
                          <th className='text-right py-4 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider'>Actions</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-slate-50 dark:divide-slate-800/50'>
                        {joinRequests.map((request) => (
                          <tr key={request.id} className='hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors'>
                            <td className='py-4 px-4'>
                              <div className='flex items-center gap-3'>
                                <div className='w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold'>
                                  {request.first_name?.[0]}{request.last_name?.[0]}
                                </div>
                                <div>
                                  <div className='font-bold text-slate-800 dark:text-white'>
                                    {request.first_name} {request.last_name}
                                  </div>
                                  <div className='text-xs text-slate-500'>{request.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className='py-4 px-4 text-sm text-slate-500'>
                              {new Date(request.created_at).toLocaleDateString()}
                            </td>
                            <td className='py-4 px-4 text-right'>
                              <div className='flex justify-end gap-2'>
                                <Button 
                                  size='sm' 
                                  variant='ghost' 
                                  onClick={() => handleRejectRequest(request.id)}
                                  className='text-destructive hover:bg-destructive/10'
                                >
                                  Reject
                                </Button>
                                <Button 
                                  size='sm' 
                                  onClick={() => handleApproveRequest(request.id)}
                                  className='bg-emerald-600 hover:bg-emerald-700 text-white'
                                >
                                  Approve
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modals are kept as they are */}
          <InviteMemberModal
            isOpen={isInviteModalOpen}
            onClose={() => setIsInviteModalOpen(false)}
            onInvite={handleInviteMembers}
            organizationId={currentOrganization?.id || sessionService.getOrganizationId()}
            existingMemberEmails={members.map(m => m.email)}
          />

          <EditRoleModal
            isOpen={isEditRoleModalOpen}
            onClose={() => setIsEditRoleModalOpen(false)}
            member={selectedMember}
            onUpdateRole={handleUpdateRole}
          />

          <RemoveMemberModal
            isOpen={isRemoveModalOpen}
            onClose={() => setIsRemoveModalOpen(false)}
            member={selectedMember}
            onRemove={handleConfirmRemove}
          />

          <MemberProfileModal
            isOpen={isProfileModalOpen}
            onClose={() => setIsProfileModalOpen(false)}
            member={selectedMember}
          />
        </div>
      </main>
    </div>
  );
};

export default TeamMembers;